import { Button } from '@mantine/core';
import { ComponentType, createElement } from 'react';
import { ElementFactory, Question, Serializer, SvgRegistry } from 'survey-core';
import { localization } from 'survey-creator-core';
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui';

const CUSTOM_TYPE = 'button';

export class QuestionButtonModel extends Question {
  get label() {
    return this.getPropertyValue('label');
  }

  set label(val) {
    this.setPropertyValue('label', val);
  }

  get onClick() {
    return this.getPropertyValue('onClick');
  }

  set onClick(val) {
    this.setPropertyValue('onClick', val);
  }

  get color() {
    return this.getPropertyValue('color');
  }

  set color(val) {
    this.setPropertyValue('color', val);
  }

  get size() {
    return this.getPropertyValue('size');
  }

  set size(val) {
    this.setPropertyValue('size', val);
  }

  get rounded() {
    return this.getPropertyValue('rounded');
  }

  set rounded(val) {
    this.setPropertyValue('rounded', val);
  }

  get href() {
    return this.getPropertyValue('href');
  }

  set href(val) {
    this.setPropertyValue('href', val);
  }

  get variant() {
    return this.getPropertyValue('variant');
  }

  set variant(val) {
    this.setPropertyValue('variant', val);
  }

  get endIcon() {
    return this.getPropertyValue('endIcon');
  }

  set endIcon(val) {
    this.setPropertyValue('endIcon', val);
  }

  get startIcon() {
    return this.getPropertyValue('startIcon');
  }

  set startIcon(val) {
    this.setPropertyValue('startIcon', val);
  }

  getType() {
    return CUSTOM_TYPE;
  }
}

export function registerButton() {
  return ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
    return new QuestionButtonModel(name) as Question;
  });
}

// Specify display names for the question type and its properties
const locale = localization.getLocale('');
locale.qt[CUSTOM_TYPE] = 'Button';

// Register an SVG icon for the question type
const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M448 96c17.7 0 32 14.3 32 32l0 256c0 17.7-14.3 32-32 32L64 416c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l384 0zM64 64C28.7 64 0 92.7 0 128L0 384c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64L64 64z"/></svg>`;
SvgRegistry.registerIconFromSvg(CUSTOM_TYPE, svg);

const properties = [
  {
    name: 'label',
    category: 'general',
    visibleIndex: 3,
    default: 'My Button'
  },
  {
    name: 'color',
    choices: ['blue', 'green', 'red', 'cyan', 'yellow', 'gray'],
    category: 'general',
    visibleIndex: 3
  },
  { name: 'href', category: 'general', visibleIndex: 3 },
  { name: 'rounded:boolean', category: 'general', visibleIndex: 3 },
  {
    name: 'variant',
    choices: [
      { value: 'filled', text: 'Filled' },
      { value: 'outline', text: 'Outline' },
      { value: 'light', text: 'Light' },
      { value: 'subtle', text: 'Subtle' },
      { value: 'transparent', text: 'Transparent' }
    ],
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'size',
    choices: [
      { value: 'xs', text: 'XS' },
      { value: 'sm', text: 'Small' },
      { value: 'md', text: 'Medium' },
      { value: 'lg', text: 'Large' },
      { value: 'xl', text: 'XL' }
    ],
    category: 'general',
    visibleIndex: 3
  },
  {
    name: 'onClick:expression',
    category: 'general',
    visibleIndex: 3,
    default: ''
  },
  { name: 'startIcon' },
  { name: 'endIcon' }
];

Serializer.addClass(
  CUSTOM_TYPE,
  properties,
  function () {
    return new QuestionButtonModel('');
  },
  'question'
);

Serializer.addProperties(CUSTOM_TYPE, properties);

class SurveyQuestionButton extends SurveyQuestionElementBase {
  constructor(props: unknown) {
    super(props);

    this.state = { value: this.question.value };
  }

  get question() {
    return this.questionBase as Question;
  }

  get label() {
    return this.question.label;
  }

  get variant() {
    return this.question.variant;
  }

  get startIcon() {
    return this.question.startIcon;
  }

  get endIcon() {
    return this.question.endIcon;
  }

  get color() {
    return this.question.color;
  }

  get href() {
    return this.question.href;
  }

  get rounded() {
    return this.question.rounded;
  }

  get size() {
    return this.question.size;
  }

  get onClick() {
    return this.question.onClick;
  }

  // Support the read-only and design modes
  get style() {
    return this.question['readOnly'] ? { pointerEvents: 'none' } : undefined;
  }

  handleChange = (data: unknown) => {
    this.question.value = data;
  };

  renderElement() {
    return createElement(
      Button as ComponentType<unknown>,
      {
        variant: this.variant,
        color: this.color,
        leftSection: this.startIcon,
        rightSection: this.endIcon,
        ...(this.href && { component: 'a', href: this.href }),
        size: this.size,
        radius: this.rounded ? 'xl' : undefined,
        style: this.style,
        onClick: () => (this.onClick ? this.question.survey.runExpression(this.onClick) : {})
      },
      this.label
    );
  }
}

export default ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) =>
  createElement(SurveyQuestionButton, props)
);
